import { useEffect, useState } from "react";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import ProductService from "../../../../service/ProductService";
import { resolveThemeImageUrl } from "../../../../utilities/resolveThemeImageUrl";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import { AdminApiError, getErrorCode } from "../../hooks/useAdminList";
import { getToken, redirectIfUnauthorized } from "../../utils/adminUtils";
import FormDialog from "../../components/form/FormDialog";
import FormField from "../../components/form/FormField";
import CroppedImageField from "../../components/form/CroppedImageField";

const OK_CODES = [200000, 200001];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = {
  logoRaw: null,
  faviconRaw: null,
  logoFile: null,
  faviconFile: null,
  footerAddress: "",
  footerEmail: "",
  footerTelephone: "",
};

const uploadImage = async (file) => {
  const res = await new ProductService().postUploadImage(getToken(), file);
  if (res?.messageCode !== 200000 || !res?.result) throw new AdminApiError(res?.messageCode ?? 500000);
  return res.result;
};

// Personalização do tema da sub-rede: logo, favicon e dados do rodapé
const ThemeDialog = ({ subnet, onClose }) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const [form, setForm] = useState(emptyForm);
  const [loadingTheme, setLoadingTheme] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!subnet) return undefined;
    let mounted = true;
    setForm(emptyForm);
    setErrors({});
    setLoadingTheme(true);
    new AdminService()
      .getCurrentTheme(getToken(), subnet.id, subnet.username)
      .then((res) => {
        if (!mounted) return;
        if (res?.messageCode && res.messageCode !== 200000) {
          showErrorToast({ code: res.messageCode });
          return;
        }
        const theme = res?.result || {};
        // Campos do rodapé podem vir nulos da API (no clássico isso quebrava o salvar)
        setForm({
          ...emptyForm,
          logoRaw: theme.themeLogo || null,
          faviconRaw: theme.themeFavicon || null,
          footerAddress: theme.ownerAddress || "",
          footerEmail: theme.ownerEmail || "",
          footerTelephone: theme.ownerTelephone || "",
        });
      })
      .catch((e) => {
        if (!mounted || redirectIfUnauthorized(e)) return;
        console.log(e);
        showErrorToast({ detail: "adminWhitelabelThemeLoadError" });
      })
      .finally(() => mounted && setLoadingTheme(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subnet]);

  const update = (changes) => setForm((prev) => ({ ...prev, ...changes }));

  const save = async () => {
    const footerEmail = form.footerEmail.trim();
    const hasSomething =
      form.logoFile ||
      form.faviconFile ||
      form.footerAddress.trim() ||
      footerEmail ||
      form.footerTelephone.trim();
    if (!hasSomething) {
      showErrorToast({ detail: "adminWhitelabelThemeEmpty" });
      return;
    }
    if (footerEmail && !EMAIL_PATTERN.test(footerEmail)) {
      setErrors({ footerEmail: localeText("adminWhitelabelInvalidEmail") });
      return;
    }

    setSaving(true);
    try {
      const logoUrl = form.logoFile ? await uploadImage(form.logoFile) : form.logoRaw;
      const faviconUrl = form.faviconFile ? await uploadImage(form.faviconFile) : form.faviconRaw;

      const payload = {
        subnetId: subnet.id,
        username: subnet.username,
        footerAddress: form.footerAddress.trim(),
        footerEmail,
        footerTelephone: form.footerTelephone.trim(),
      };
      if (logoUrl) payload.logoUrl = logoUrl;
      if (faviconUrl) payload.faviconUrl = faviconUrl;

      const res = await new AdminService().customizeTheme(getToken(), payload);
      if (!OK_CODES.includes(res?.messageCode)) throw new AdminApiError(res?.messageCode ?? 500000);

      showSuccessToast({ detail: "adminWhitelabelThemeSaved" });
      onClose();
    } catch (e) {
      if (redirectIfUnauthorized(e)) return;
      console.log(e);
      showErrorToast({
        code: getErrorCode(e),
        message: e?.response?.data?.message,
        fallbackMessage: localeText("adminWhitelabelThemeSaveError"),
      });
    } finally {
      setSaving(false);
    }
  };

  const logoPreview = form.logoRaw ? resolveThemeImageUrl(form.logoRaw) || form.logoRaw : null;
  const faviconPreview = form.faviconRaw
    ? resolveThemeImageUrl(form.faviconRaw) || form.faviconRaw
    : null;

  return (
    <FormDialog
      visible={!!subnet}
      title={localeText("personalizeSubnetTheme")}
      subtitle={subnet?.name}
      saving={saving}
      onSave={save}
      onCancel={() => !saving && onClose()}
      width="40rem"
    >
      {loadingTheme ? (
        <div className="admin-whitelabel__loading">
          <i className="pi pi-spin pi-spinner" aria-hidden="true" />
          <span className="admin-muted">{localeText("loading")}</span>
        </div>
      ) : (
        <>
          <fieldset className="admin-form__fieldset">
            <legend className="admin-form__legend">{localeText("adminWhitelabelVisualIdentity")}</legend>
            <div className="admin-form">
              <CroppedImageField
                label="Logo"
                ratio={4}
                ratioLabel="4:1"
                currentUrl={logoPreview}
                file={form.logoFile}
                onChange={(logoFile) => update({ logoFile })}
                onError={(message) => showErrorToast({ detail: message })}
                previewClassName="admin-cropped-image__preview--logo"
                disabled={saving}
              />
              <CroppedImageField
                label="Favicon"
                ratio={1}
                ratioLabel="1:1"
                hint={localeText("adminWhitelabelFaviconHint")}
                currentUrl={faviconPreview}
                file={form.faviconFile}
                onChange={(faviconFile) => update({ faviconFile })}
                onError={(message) => showErrorToast({ detail: message })}
                previewClassName="admin-cropped-image__preview--favicon"
                disabled={saving}
              />
            </div>
          </fieldset>

          <fieldset className="admin-form__fieldset">
            <legend className="admin-form__legend">{localeText("adminWhitelabelFooter")}</legend>
            <p className="admin-muted admin-whitelabel__note">{localeText("footerFieldsInfo")}</p>
            <div className="admin-form">
              <FormField label={localeText("address")} htmlFor="theme-footer-address">
                <input
                  id="theme-footer-address"
                  className="admin-input"
                  value={form.footerAddress}
                  onChange={(e) => update({ footerAddress: e.target.value })}
                  autoComplete="off"
                />
              </FormField>
              <div className="admin-form__grid">
                <FormField
                  label={localeText("email")}
                  htmlFor="theme-footer-email"
                  error={errors.footerEmail}
                >
                  <input
                    id="theme-footer-email"
                    type="email"
                    className="admin-input"
                    value={form.footerEmail}
                    onChange={(e) => {
                      update({ footerEmail: e.target.value });
                      setErrors({});
                    }}
                    autoComplete="off"
                  />
                </FormField>
                <FormField label={localeText("phone")} htmlFor="theme-footer-phone">
                  <input
                    id="theme-footer-phone"
                    type="tel"
                    className="admin-input"
                    value={form.footerTelephone}
                    onChange={(e) => update({ footerTelephone: e.target.value })}
                    autoComplete="off"
                  />
                </FormField>
              </div>
            </div>
          </fieldset>
          <p className="admin-muted admin-whitelabel__note">{localeText("logoOrFavicon")}</p>
        </>
      )}
    </FormDialog>
  );
};

export default ThemeDialog;
